/**
 * #10 結帳頁 — LIFF /checkout
 *
 * 顯示:
 *  - 訂單摘要(小計)
 *  - 寄送地址:沿用 member.address,可勾「使用其他地址」展開三欄編輯
 *  - 付款方式:LINE Pay / 綠界(都 disabled 標「商戶申請中」)
 *  - 「下單(模擬)」按鈕 → POST /api/orders → 跳 /orders/:id
 *
 * Phase 1 純 mock 流程,不接金流 — Phase 2 接通後 swap。
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "../store/cartStore";
import { useMemberStore } from "../store/memberStore";
import { useUIStore } from "../store/uiStore";
import { getMe } from "../api/members";
import { getOrder, placeOrder, validateCode, type CodeValidation } from "../api/cart";
import { getPaymentMethods, jkopayCheckout, jkopayErrorText } from "../api/payments";
import { liffSdk } from "../liff";
import { TAIWAN_CITIES, getDistricts } from "../data/tw-regions";
import { needsProfile, withNext } from "../utils/join";
// 街口官方 App Icon — 取自街口 CIS Guideline(2025-06 版)PDF 原始素材;
// CIS 規範:寬 <30px 場合用 ICON、可加註文案輔助、不可改色/變形
import jkopayIcon from "../assets/jkopay-icon.png";

// 「使用其他地址」記憶 — 下單成功後存本機 localStorage,下次進結帳自動帶入。
// 只記在裝置上、不寫會員資料(勾選框承諾「不影響會員資料」)。
const LAST_ADDR_KEY = "jy_checkout_last_addr";


export default function Checkout() {
  const navigate = useNavigate();
  const cart = useCartStore((s) => s.cart);
  const refreshCart = useCartStore((s) => s.refresh);
  const member = useMemberStore((s) => s.member);
  const showToast = useUIStore((s) => s.showToast);

  // 地址覆寫 — 預設 OFF,user 勾才展開編輯;勾的話用 form 三欄,送單時送 address_override。
  // 上次填過的會從 localStorage 帶入(見下方 useEffect)。
  const [overrideAddr, setOverrideAddr] = useState(false);
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [detail, setDetail] = useState("");
  const addrPrefilled = useRef(false);

  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 冪等 key — 存「當前這筆下單意圖」。失敗自動重試沿用同 key(後端冪等,不會重複成立 / 入帳);
  // 成功才清空,下一筆 = 全新 key。重試仍失敗則保留,使用者稍後再按仍用同 key。
  const idempotencyKey = useRef<string | null>(null);

  // 推薦碼 / 優惠碼
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState<CodeValidation | null>(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState("");

  // 街口支付 — methods 由後端判定(UAT 期間只有測試名單開);下單成功後進付款面板
  const [jkopayOn, setJkopayOn] = useState(false);
  const [payOrderId, setPayOrderId] = useState("");
  const [payUrl, setPayUrl] = useState("");
  const [payAmount, setPayAmount] = useState(0);
  const [payRefreshing, setPayRefreshing] = useState(false);

  useEffect(() => {
    (async () => {
      await refreshCart();
      if (!member) {
        const m = await getMe();
        useMemberStore.getState().setMember(m);
      }
      try {
        const m = await getPaymentMethods();
        setJkopayOn(!!m.jkopay);
      } catch {
        setJkopayOn(false); // 拿不到就當沒開,fallback 回 mock 流程
      }
    })();
  }, [refreshCart, member]);

  // 一鍵加入(2026-09-24)的會員還沒留姓名 / 手機 → 出貨對不到人,先去補、補完回結帳。
  // 後端 place_order 也擋(profile_required),這裡先攔是讓人不用填到一半才被彈走。
  useEffect(() => {
    if (needsProfile(member)) navigate(withNext("/complete-profile", "/checkout"), { replace: true });
  }, [member, navigate]);

  // 帶入上次填過的「其他地址」— 會員本身沒設地址時直接展開,免得每次重勾重填
  useEffect(() => {
    if (!member || addrPrefilled.current) return;
    addrPrefilled.current = true;
    try {
      const saved = JSON.parse(localStorage.getItem(LAST_ADDR_KEY) || "null");
      if (!saved?.city || !saved?.detail || !TAIWAN_CITIES.includes(saved.city)) return;
      setCity(saved.city);
      setDistrict(getDistricts(saved.city).includes(saved.district) ? saved.district : "");
      setDetail(saved.detail);
      const a = member.address || {};
      if (!a.city && !a.district && !a.detail) setOverrideAddr(true);
    } catch { /* 讀不到就照舊手填 */ }
  }, [member]);

  // 付款面板輪詢 — 街口 callback 入帳後 status 離開 pending_payment 就跳訂單頁
  useEffect(() => {
    if (!payOrderId) return;
    const t = setInterval(async () => {
      try {
        const o = await getOrder(payOrderId);
        if (o.status && o.status !== "pending_payment") {
          clearInterval(t);
          showToast("付款完成!", "success");
          navigate(`/orders/${payOrderId}`, { replace: true });
        }
      } catch { /* 網路抖動,下一輪再試 */ }
    }, 3000);
    return () => clearInterval(t);
  }, [payOrderId, navigate, showToast]);

  function openJkopay() {
    // LINE 內建瀏覽器不能自動轉導(會被 webview 吃掉開不了街口 App)—
    // 必須使用者點擊 + external 外開,這是街口文件的硬要求
    if (!payUrl) return;
    try {
      liffSdk().openWindow({ url: payUrl, external: true });
    } catch {
      window.open(payUrl, "_blank");
    }
  }

  async function refreshPayUrl() {
    // payment_url 20 分鐘失效 — 重打 checkout 續期(後端同單冪等)
    if (!payOrderId || payRefreshing) return;
    setPayRefreshing(true);
    try {
      const r = await jkopayCheckout(payOrderId);
      if (r.status === "ok" && r.payment_url) {
        setPayUrl(r.payment_url);
        showToast("付款連結已更新", "success");
      } else if (r.status === "already_paid") {
        navigate(`/orders/${payOrderId}`, { replace: true });
      } else {
        showToast(jkopayErrorText(r), "error");
      }
    } catch {
      showToast("取得付款連結失敗,請稍後再試", "error");
    } finally {
      setPayRefreshing(false);
    }
  }

  if (!cart || !member) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }

  // 街口付款面板 — 訂單已成立(cart 已清),等使用者完成付款;輪詢到入帳自動跳轉
  if (payOrderId) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <header className="bg-primary text-white px-5 py-6 text-center">
          <h1 className="text-xl font-bold">完成付款</h1>
          <div className="text-sm opacity-80 mt-1">訂單編號 #{payOrderId}</div>
        </header>
        <div className="flex-1 px-5 py-8 flex flex-col items-center justify-center text-center gap-6">
          <div>
            <div className="text-sm text-muted mb-1">應付金額</div>
            <div className="text-3xl font-bold text-ink">NT$ {payAmount.toLocaleString()}</div>
          </div>
          <button onClick={openJkopay} disabled={!payUrl} className="btn-primary bg-jkopay w-full text-lg py-4">
            {payUrl ? "前往街口支付" : "付款連結準備中…"}
          </button>
          <div className="text-xs text-muted leading-relaxed">
            點擊按鈕將開啟街口支付 App 完成付款。<br />
            付款完成後請回到本頁,系統確認入帳會自動更新。
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <button onClick={refreshPayUrl} disabled={payRefreshing} className="text-primary underline">
              {payRefreshing ? "更新中…" : "付款連結失效?重新取得"}
            </button>
            <button onClick={() => navigate(`/orders/${payOrderId}`, { replace: true })} className="text-muted underline">
              稍後再付,先查看訂單
            </button>
          </div>
        </div>
      </div>
    );
  }

  // cart 是空的 — 防止 user 直接打 /checkout URL 來這頁
  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center text-muted gap-4">
        <div>購物車目前為空</div>
        <button onClick={() => navigate("/products")} className="btn-primary">返回商品頁</button>
      </div>
    );
  }

  const memberAddr = member.address || {};
  const memberAddrLine = [memberAddr.city, memberAddr.district, memberAddr.detail].filter(Boolean).join(" ");
  const hasMemberAddr = !!memberAddrLine;

  const discount = applied?.discount_amount || 0;
  const payTotal = Math.max(0, cart.total - discount);

  async function applyCode() {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setCodeChecking(true);
    setCodeError("");
    try {
      const r = await validateCode(c);
      if (r.status === "ok") {
        setApplied(r);
        setCode(c);
      } else {
        setApplied(null);
        setCodeError(codeErrorMsg(r.status));
      }
    } catch {
      setCodeError("驗證失敗,請稍後再試");
    } finally {
      setCodeChecking(false);
    }
  }

  function clearCode() {
    setApplied(null);
    setCode("");
    setCodeError("");
  }

  async function submit() {
    if (overrideAddr) {
      if (!city || !detail) {
        showToast("請完整填寫寄送地址", "error");
        return;
      }
    } else if (!hasMemberAddr) {
      showToast("會員資料尚未設定地址,請啟用「使用其他地址」", "error");
      return;
    }

    // 產 / 沿用冪等 key —— ref 為空才產新的,重試與下一次重按都沿用同一個
    if (!idempotencyKey.current) {
      idempotencyKey.current = crypto.randomUUID();
    }

    // 送出一次(帶當前 idempotency_key)。回傳代表後端有回應(成功 or 業務狀態);
    // axios throw(逾時 / 5xx / 斷線)會往外拋,由 submit 接住後重試。
    const postOnce = () =>
      placeOrder({
        payment_method: jkopayOn ? "jkopay" : "mock",
        note: note.trim(),
        address_override: overrideAddr ? { city, district, detail } : undefined,
        code: applied?.code || code.trim() || undefined,
        idempotency_key: idempotencyKey.current!,
      });

    // 處理後端回應(成功 + 各業務狀態)。回 true = 已收斂(成功),呼叫端清空 key。
    const handleResult = (r: { status: string; order_id?: string; code_status?: string }): boolean => {
      if (r.status === "ok" && r.order_id) {
        if (overrideAddr && city && detail) {
          try { localStorage.setItem(LAST_ADDR_KEY, JSON.stringify({ city, district, detail })); } catch { /* 存不了就算了 */ }
        }
        if (jkopayOn) {
          // 街口單:訂單已成立,續拿付款網址進付款面板(不離開本頁)
          const oid = r.order_id;
          setPayAmount(payTotal);
          setPayOrderId(oid);
          showToast("訂單已成立,請完成付款", "success");
          jkopayCheckout(oid)
            .then((p) => {
              if (p.status === "ok" && p.payment_url) setPayUrl(p.payment_url);
              else if (p.status === "already_paid") navigate(`/orders/${oid}`, { replace: true });
              else showToast(jkopayErrorText(p), "error");
            })
            .catch(() => showToast("取得付款連結失敗,請點「重新取得」", "error"));
        } else {
          showToast("訂單已成立", "success");
          navigate(`/orders/${r.order_id}`, { replace: true });
        }
        return true;
      } else if (r.status === "invalid_code") {
        // 業務狀態:推薦碼問題,非網路失敗 —— 維持既有處理,不重試
        showToast(`推薦碼無效:${codeErrorMsg(r.code_status || "")}`, "error");
      } else if (r.status === "profile_required") {
        // 還沒留姓名 / 手機(理論上進頁時就攔了,這是後端的最後防線)→ 去補,補完回來
        showToast("結帳前請先補齊姓名與手機", "info");
        navigate(withNext("/complete-profile", "/checkout"));
      } else {
        showToast(`下單未成功:${r.status}`, "error");
      }
      return false;
    };

    setSubmitting(true);
    try {
      try {
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;   // 成功 → 下一筆全新 key
      } catch {
        // 第一次 axios throw —— 用同一個 key 自動重試一次。
        // 後端冪等:第一次若其實已成立,重試拿回同一筆;若真沒成立,重試才建立。
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;
      }
    } catch {
      // 重試仍失敗 —— 用對帳語氣,不宣告失敗;不清空 key(稍後再按仍用同 key,不會重複成立)
      showToast("訂單可能已成立,請至訂單查詢確認", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-32">
      <header className="bg-primary text-white px-5 py-6 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">結帳</h1>
      </header>

      <div className="px-5 mt-5 space-y-4">
        {/* 訂單摘要 */}
        <div className="card">
          <div className="text-sm text-muted mb-3 font-medium">訂單摘要</div>
          <div className="space-y-2 text-sm">
            {cart.items.map((it) => (
              <div key={it.product_id + it.mode} className="flex justify-between">
                <span className="flex-1">
                  {it.name} <span className="text-muted">× {it.qty}</span>
                </span>
                <span className="text-ink">NT$ {it.subtotal.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-line mt-3 pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted">小計</span>
              <span>NT$ {cart.total.toLocaleString()}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-coral">
                <span>折扣{applied?.code ? `(${applied.code})` : ""}</span>
                <span>− NT$ {discount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted">應付</span>
              <span className="font-bold text-ink">NT$ {payTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* 推薦碼 / 優惠碼 */}
        <div className="card space-y-2">
          <div className="text-sm text-muted font-medium">推薦碼 / 優惠碼(選填)</div>
          {!applied ? (
            <>
              <div className="flex gap-2">
                <input
                  className="input flex-1 uppercase"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(""); }}
                  placeholder="輸入推薦碼或優惠碼"
                  maxLength={20}
                />
                <button
                  onClick={applyCode}
                  className="btn-secondary px-4 shrink-0"
                  disabled={codeChecking || !code.trim()}
                >
                  {codeChecking ? "驗證中…" : "套用"}
                </button>
              </div>
              {codeError && <div className="text-xs text-coral">{codeError}</div>}
            </>
          ) : (
            <div className="flex items-center justify-between bg-sage-light/20 rounded-lg px-3 py-2">
              <div className="text-sm">
                <span className="font-mono font-medium">{applied.code}</span>
                <span className="text-primary ml-2">{applied.message}</span>
              </div>
              <button onClick={clearCode} className="text-xs text-coral underline shrink-0 ml-2">移除</button>
            </div>
          )}
        </div>

        {/* 寄送地址 */}
        <div className="card space-y-3">
          <div className="text-sm text-muted font-medium">寄送地址</div>
          {hasMemberAddr && !overrideAddr && (
            <div className="text-sm text-ink">{memberAddrLine}</div>
          )}
          {!hasMemberAddr && !overrideAddr && (
            <div className="text-sm text-coral">會員資料尚未設定地址,請勾選下方使用其他地址</div>
          )}
          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={overrideAddr}
              onChange={(e) => setOverrideAddr(e.target.checked)}
            />
            <span>使用其他地址(本次訂單用,不影響會員資料)</span>
          </label>
          {overrideAddr && (
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="co-city">縣市</label>
                <select
                  id="co-city"
                  className="input"
                  value={city}
                  onChange={(e) => { setCity(e.target.value); setDistrict(""); }}
                >
                  <option value="">請選擇縣市</option>
                  {TAIWAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="co-district">區域</label>
                <select
                  id="co-district"
                  className="input"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  disabled={!city}
                >
                  <option value="">{city ? "請選擇區域" : "請先選擇縣市"}</option>
                  {getDistricts(city).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="co-detail">詳細地址</label>
                <textarea
                  id="co-detail"
                  className="input resize-none"
                  rows={2}
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="路名、巷弄、號碼、樓層"
                  disabled={!city}
                />
              </div>
            </div>
          )}
        </div>

        {/* 付款方式 — 街口開通(全域 or 測試名單)走真金流;否則維持 Phase 1 mock stub */}
        <div className="card space-y-3">
          <div className="text-sm text-muted font-medium">付款方式</div>
          {jkopayOn ? (
            <>
              <label className="flex items-center gap-3 text-sm">
                <input type="radio" checked readOnly />
                <img src={jkopayIcon} alt="" className="w-6 h-6" />
                <span>街口支付</span>
              </label>
              <div className="text-xs text-muted bg-sage-light/20 p-3 rounded-lg">
                下單後將開啟街口支付完成付款;付款完成訂單才會成立出貨。
              </div>
            </>
          ) : (
            <>
              <label className="flex items-center gap-3 text-sm text-muted cursor-not-allowed">
                <input type="radio" disabled />
                <span>LINE Pay <span className="text-xs text-coral ml-2">(商戶申請中,即將開放)</span></span>
              </label>
              <label className="flex items-center gap-3 text-sm text-muted cursor-not-allowed">
                <input type="radio" disabled />
                <span>綠界 ECPay <span className="text-xs text-coral ml-2">(商戶申請中,即將開放)</span></span>
              </label>
              <div className="text-xs text-muted bg-sage-light/20 p-3 rounded-lg">
                本訂單為模擬下單(資料已記錄),金流接通後將通知您完成付款。
              </div>
            </>
          )}
        </div>

        {/* 備註 */}
        <div className="card">
          <div className="text-sm text-muted font-medium mb-2">備註(選填)</div>
          <textarea
            className="input resize-none"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="如有特殊需求請告知"
          />
        </div>
      </div>

      {/* 底部固定下單按鈕 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-line px-5 py-4 shadow-lg">
        <button
          onClick={submit}
          disabled={submitting}
          className="btn-primary w-full"
        >
          {submitting
            ? "下單中…"
            : jkopayOn
              ? `下單並付款 · NT$ ${payTotal.toLocaleString()}`
              : `下單(模擬) · NT$ ${payTotal.toLocaleString()}`}
        </button>
      </div>
    </div>
  );
}


/** 碼驗證失敗 status → 中文訊息(對齊後端 code_service.validate 回傳) */
function codeErrorMsg(status: string): string {
  switch (status) {
    case "not_found": return "查無此碼";
    case "inactive": return "此碼已停用";
    case "own_code": return "不能使用自己的推薦碼";
    case "not_new_customer": return "此推薦碼僅限新客首單";
    case "expired": return "此優惠碼已過期";
    case "not_started": return "此優惠碼尚未開始";
    case "usage_exceeded": return "此優惠碼已達使用上限";
    case "empty": return "請輸入推薦碼";
    default: return "推薦碼無效";
  }
}
